import { isAbsolute } from 'node:path'
import type { Context } from '@tnega/core'
import {
  localExecutionProvider,
  type ExecutionProvider,
  type ProcessResult,
} from '@tnega/execution'
import {
  DEFAULT_SEARCH_EXCLUDES,
  DEFAULT_SEARCH_MAX_RESULTS,
  DEFAULT_SEARCH_OUTPUT_MAX_BYTES,
  DEFAULT_SEARCH_TIMEOUT_MS,
  SearchError,
  SearchService,
  type FindFilesRequest,
  type FindFilesResult,
  type FindFilesSpec,
  type SearchSpecBase,
  type SearchTextRequest,
  type SearchTextResult,
  type SearchTextSpec,
} from '@tnega/search'
import { buildGlobArgv, buildGrepArgv, buildGrepFilesArgv, buildListArgv } from './argv.js'
import { globMatcher, matchesRootOnly, relativeToRoot } from './glob.js'
import { resolveRipgrepPath } from './binary.js'
import { assertSearchSucceeded, parseGlobPaths, parseGrepMatches } from './parse.js'

/** ripgrep 的本次实现可配置项；未给的字段取 Service Definition 的能力默认值。 */
export interface Config {
  /** 工作区根，搜索进程的工作目录。 */
  cwd?: string
  /** ripgrep 二进制路径；未给时在 `PATH` 上找 `rg`。 */
  ripgrepPath?: string
  /** 是否让工作区 `.gitignore` 过滤搜索。 */
  respectGitignore?: boolean
  /** 遍历时跳过的目录名。 */
  excludes?: readonly string[]
  /** 时间预算（毫秒）。 */
  timeoutMs?: number
  /** 原始输出上限（字节）。 */
  maxOutputBytes?: number
  /** 结果条数上限。 */
  maxResults?: number
  /** 进程执行边界；默认本机实现。 */
  execution?: ExecutionProvider
}

interface ResolvedConfig {
  cwd: string
  ripgrepPath: string | undefined
  respectGitignore: boolean
  excludes: readonly string[]
  timeoutMs: number
  maxOutputBytes: number
  maxResults: number
  execution: ExecutionProvider
}

function normalizeConfig(config: Config): ResolvedConfig {
  return {
    cwd: config.cwd ?? process.cwd(),
    ripgrepPath: config.ripgrepPath,
    respectGitignore: config.respectGitignore ?? true,
    excludes: [...(config.excludes ?? DEFAULT_SEARCH_EXCLUDES)],
    timeoutMs: config.timeoutMs ?? DEFAULT_SEARCH_TIMEOUT_MS,
    maxOutputBytes: config.maxOutputBytes ?? DEFAULT_SEARCH_OUTPUT_MAX_BYTES,
    maxResults: config.maxResults ?? DEFAULT_SEARCH_MAX_RESULTS,
    execution: config.execution ?? localExecutionProvider,
  }
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

/**
 * 把请求里的搜索根规范成工作区相对路径，并拒绝逃逸。
 *
 * @throws SearchError `SEARCH_INVALID_PATH`。
 */
function resolveRoot(input: string | undefined): string {
  if (input === undefined) return '.'
  const normalized = input.replaceAll('\\', '/')
  const segments = normalized.split('/').filter(segment => segment !== '' && segment !== '.')
  const driveRelative = /^[A-Za-z]:/.test(normalized)
  if (isAbsolute(input) || driveRelative || segments.includes('..')) {
    throw new SearchError(
      `search root must stay inside the workspace: ${input}`,
      'SEARCH_INVALID_PATH',
    )
  }
  return segments.length > 0 ? segments.join('/') : '.'
}

/**
 * ripgrep 支撑的工作区搜索 Provider。`rg` 永远以固定 argv 向量启动，模型可控的值
 * 都是独立参数，中间没有 shell 层。
 *
 * 本类只实现机制：`resolve*` 落定默认值，`runFindFiles` / `runSearchText` 落进程。
 * `search/pre-search`、`search/post-search`、`search/result`、`search/error` 由
 * `SearchService` 的模板方法统一派发，这里看不见也不需要知道事件名。
 */
export class RipgrepSearch extends SearchService {
  private readonly _config: ResolvedConfig

  constructor(ctx: Context, config: Config = {}) {
    super(ctx)
    this._config = normalizeConfig(config)
  }

  override resolveFindFiles(request: FindFilesRequest): FindFilesSpec {
    return {
      ...this._resolveBase(request),
      pattern: request.pattern,
    }
  }

  override resolveSearchText(request: SearchTextRequest): SearchTextSpec {
    return {
      ...this._resolveBase(request),
      pattern: request.pattern,
      ...(request.glob !== undefined ? { glob: request.glob } : {}),
    }
  }

  protected override async runFindFiles(spec: FindFilesSpec): Promise<FindFilesResult> {
    const paths = spec.respectGitignore
      ? await this._listMatching('findFiles', spec, spec.pattern)
      : parseGlobPaths('findFiles', await this._run('findFiles', buildGlobArgv(spec.pattern, commandOptions(spec)), spec))
    return {
      paths: paths.slice(0, spec.maxResults),
      truncated: paths.length > spec.maxResults,
    }
  }

  protected override async runSearchText(spec: SearchTextSpec): Promise<SearchTextResult> {
    const matches = spec.glob !== undefined && spec.respectGitignore
      ? await this._searchMatchingFiles(spec, spec.glob)
      : parseGrepMatches('searchText', await this._run('searchText', buildGrepArgv(spec.pattern, commandOptions(spec)), spec))
    return {
      matches: matches.slice(0, spec.maxResults),
      truncated: matches.length > spec.maxResults,
    }
  }

  /**
   * 列出 ripgrep 在搜索根下会搜索的文件（遵守忽略规则），再按 glob 在本进程里过滤。
   * ripgrep 的正向 `--glob` 是覆盖规则，会让 .gitignore 失效，所以不交给它匹配。
   */
  private async _listMatching(label: string, spec: SearchSpecBase, pattern: string): Promise<string[]> {
    const options = commandOptions(spec)
    const stdout = await this._run(label, buildListArgv({ ...options, rootOnly: matchesRootOnly(pattern) }), spec)
    const matches = globMatcher(pattern)
    return parseGlobPaths(label, stdout).filter(path => matches(relativeToRoot(path, spec.root)))
  }

  /** 带 glob 的内容检索：先挑出匹配且未被忽略的文件，再分批只搜这些文件。 */
  private async _searchMatchingFiles(spec: SearchTextSpec, glob: string): Promise<SearchTextResult['matches']> {
    const files = await this._listMatching('searchText', spec, glob)
    const matches: SearchTextResult['matches'] = []
    for (const batch of fileBatches(files)) {
      const stdout = await this._run('searchText', buildGrepFilesArgv(spec.pattern, batch, commandOptions(spec)), spec)
      matches.push(...parseGrepMatches('searchText', stdout))
      // One past the cap is enough to report truncation.
      if (matches.length > spec.maxResults) break
    }
    return matches
  }

  /** 默认值与上限唯一的落点：run 方法里不再出现 `?? 默认值`。 */
  private _resolveBase(request: FindFilesRequest | SearchTextRequest): SearchSpecBase {
    return {
      root: resolveRoot(request.path),
      maxResults: request.maxResults ?? this._config.maxResults,
      excludes: this._config.excludes,
      respectGitignore: this._config.respectGitignore,
      maxOutputBytes: this._config.maxOutputBytes,
      timeoutMs: this._config.timeoutMs,
      ...(request.signal ? { signal: request.signal } : {}),
    }
  }

  private async _run(
    label: string,
    argv: readonly string[],
    spec: SearchSpecBase,
  ): Promise<string> {
    const binary = await resolveRipgrepPath(this._config.ripgrepPath)
    if (spec.signal?.aborted) {
      throw new SearchError(`${label} was aborted before it could start`, 'SEARCH_ABORTED')
    }
    let result: ProcessResult
    try {
      result = await this._config.execution.runProcess({
        argv: [binary, ...argv],
        cwd: this._config.cwd,
        timeoutMs: spec.timeoutMs,
        maxBuffer: spec.maxOutputBytes,
        ...(spec.signal ? { signal: spec.signal } : {}),
      })
    } catch (error) {
      if (spec.signal?.aborted) {
        throw new SearchError(`${label} was aborted before completion`, 'SEARCH_ABORTED', { cause: error })
      }
      throw new SearchError(`${label} could not run: ${message(error)}`, 'SEARCH_FAILED', { cause: error })
    }
    if (spec.signal?.aborted) {
      throw new SearchError(`${label} was aborted before completion`, 'SEARCH_ABORTED')
    }
    assertSearchSucceeded(label, result, spec.maxOutputBytes)
    return result.stdout
  }
}

/**
 * 把文件列表切成若干批，使每次命令行都远低于 Windows 约 32K 字符的上限。
 */
const BATCH_CHARS = 24_000

function fileBatches(files: readonly string[]): string[][] {
  const batches: string[][] = []
  let current: string[] = []
  let size = 0
  for (const file of files) {
    if (current.length > 0 && size + file.length + 1 > BATCH_CHARS) {
      batches.push(current)
      current = []
      size = 0
    }
    current.push(file)
    size += file.length + 1
  }
  if (current.length > 0) batches.push(current)
  return batches
}

function commandOptions(spec: SearchSpecBase): {
  path?: string
  respectGitignore: boolean
  excludes: readonly string[]
} {
  return {
    ...(spec.root !== '.' ? { path: spec.root } : {}),
    respectGitignore: spec.respectGitignore,
    excludes: spec.excludes,
  }
}

export default RipgrepSearch

export { resolveRipgrepPath } from './binary.js'

export const name = '@tnega/search-ripgrep'

/** 挂载入口：`await ctx.plugin(searchRipgrep, { cwd })`。 */
export const searchRipgrep = {
  name: 'search-ripgrep',
  apply(ctx: Context, config: Config = {}) {
    new RipgrepSearch(ctx, config)
  },
}
